/**
 * Utilities for decoding and verifying JWT expiration in the proxy and client.
 */

export interface JwtPayload {
    exp?: number;
    iat?: number;
    sub?: string;
    [key: string]: unknown;
}

/**
 * Decodes the payload portion of a JWT string without verifying the signature.
 * Returns null if the token is not a valid 3-part JWT.
 */
export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
    if (!token || typeof token !== "string") return null;

    const parts = token.trim().split(".");
    if (parts.length !== 3) return null;

    try {
        let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (base64.length % 4 !== 0) {
            base64 += "=";
        }

        const jsonString = typeof atob === "function"
            ? atob(base64)
            : Buffer.from(base64, "base64").toString("utf-8");

        return JSON.parse(jsonString) as JwtPayload;
    } catch {
        return null;
    }
}

/**
 * Checks whether a given JWT token is expired based on its 'exp' claim.
 * 
 * @param token Raw JWT string (can also accept 'Bearer <token>')
 * @param clockSkewSeconds Optional clock skew allowance in seconds (default: 0)
 * @returns boolean: true if token is non-empty AND has an exp claim <= (current time + skew);
 *                   false if token is still valid, or if it does not contain a numeric exp claim.
 */
export function isJwtExpired(token: string | null | undefined, clockSkewSeconds = 0): boolean {
    if (!token) return true;

    let rawToken = token.trim();
    if (rawToken.startsWith("Bearer ")) {
        rawToken = rawToken.slice(7).trim();
    }

    if (!rawToken || rawToken === "null" || rawToken === "undefined") {
        return true;
    }

    const payload = decodeJwtPayload(rawToken);
    // If the token is not a JSON JWT or does not have a numeric exp claim,
    // it cannot be determined to be expired by timestamp.
    if (!payload || typeof payload.exp !== "number") {
        return false;
    }

    const currentEpochSeconds = Math.floor(Date.now() / 1000);
    return payload.exp <= (currentEpochSeconds + clockSkewSeconds);
}
